import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveAbout } from '../siteAbout';
import { fetchSite, getToken, patchAbout, uploadFile } from './api';
import { AdminModal, input, label } from './adminUi';
import TeamMemberModalFooter from './TeamMemberModalFooter';

function timelineYearsToText(years) {
  if (!Array.isArray(years)) return '';
  return years.join('\n');
}

function parseTimelineYears(text) {
  if (typeof text !== 'string') return [];
  return text
    .split(/[\n,]+/)
    .map((y) => y.trim())
    .filter(Boolean);
}

function emptyTeamMember() {
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `team-${Date.now()}`,
    name: '',
    role: '',
    bio: '',
    image: '',
  };
}

function teamMemberRequiredValid(d) {
  if (!d || typeof d !== 'object') return false;
  return (
    Boolean(String(d.name ?? '').trim()) &&
    Boolean(String(d.role ?? '').trim()) &&
    Boolean(String(d.bio ?? '').trim()) &&
    Boolean(String(d.image ?? '').trim())
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

const cardClass =
  'flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between';
const editBtnClass =
  'shrink-0 rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90';
const secondaryBtnClass =
  'shrink-0 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';

function ModalFooter({ onCancel, onApply, applyLabel = 'Apply' }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={editBtnClass}>
        {applyLabel}
      </button>
    </div>
  );
}

export default function AboutDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => resolveAbout(DEFAULT_SITE));
  const [timelineText, setTimelineText] = useState(() =>
    timelineYearsToText(resolveAbout(DEFAULT_SITE).journey?.timelineYears)
  );
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadKey, setUploadKey] = useState('');

  const [modal, setModal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [teamModal, setTeamModal] = useState(null);
  const [teamDraft, setTeamDraft] = useState(null);
  const teamNameInputRef = useRef(null);
  const teamRoleInputRef = useRef(null);
  const teamBioInputRef = useRef(null);
  const teamImageInputRef = useRef(null);
  const teamModalPanelRef = useRef(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => {
        const r = resolveAbout(site);
        setForm(r);
        setTimelineText(timelineYearsToText(r.journey?.timelineYears));
      })
      .catch(() => {
        const r = resolveAbout(DEFAULT_SITE);
        setForm(r);
        setTimelineText(timelineYearsToText(r.journey?.timelineYears));
      });
  }, [navigate]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function appendMember(m) {
    setForm((f) => ({
      ...f,
      managementPanel: {
        ...f.managementPanel,
        members: [...(f.managementPanel?.members || []), m],
      },
    }));
  }

  function removeMember(i) {
    if (!window.confirm('Remove this person from the management panel?')) return;
    setForm((f) => ({
      ...f,
      managementPanel: {
        ...f.managementPanel,
        members: (f.managementPanel?.members || []).filter((_, j) => j !== i),
      },
    }));
  }

  function closeEditorModal() {
    setModal(null);
    setDraft(null);
    setErr('');
  }

  function closeTeamModal() {
    setTeamModal(null);
    setTeamDraft(null);
    setErr('');
  }

  function openModal(id) {
    setErr('');
    const f = form;
    switch (id) {
      case 'pageHero':
        setDraft({ pageHeroImage: f.pageHeroImage || '' });
        break;
      case 'homeCopy':
        setDraft({
          label: f.label,
          headingLine1: f.headingLine1,
          headingLine2: f.headingLine2,
          intro: f.intro,
        });
        break;
      case 'homeVideo':
        setDraft({
          videoUrl: f.videoUrl,
          videoAriaLabel: f.videoAriaLabel,
          videoPosterUrl: f.videoPosterUrl,
        });
        break;
      case 'homeYoutube':
        setDraft({
          youtubeVideoId: f.youtubeVideoId,
          youtubeIframeTitle: f.youtubeIframeTitle,
          sidebarText: f.sidebarText,
          sidebarCtaLabel: f.sidebarCtaLabel,
          sidebarCtaHref: f.sidebarCtaHref,
        });
        break;
      case 'journey':
        setDraft({
          sectionLabel: f.journey?.sectionLabel || '',
          title: f.journey?.title || '',
          body: f.journey?.body || '',
          visualUrl: f.journey?.visualUrl || '',
          accentImageUrl: f.journey?.accentImageUrl || '',
          timelineText: timelineYearsToText(f.journey?.timelineYears),
        });
        break;
      case 'missionVision':
        setDraft({
          sectionEyebrow: f.missionVision?.sectionEyebrow || '',
          sectionTitle: f.missionVision?.sectionTitle || '',
          missionTitle: f.missionVision?.missionTitle || '',
          missionBody: f.missionVision?.missionBody || '',
          visionTitle: f.missionVision?.visionTitle || '',
          visionBody: f.missionVision?.visionBody || '',
        });
        break;
      case 'managementIntro':
        setDraft({
          sectionEyebrow: f.managementPanel?.sectionEyebrow || '',
          sectionTitle: f.managementPanel?.sectionTitle || '',
          intro: f.managementPanel?.intro || '',
        });
        break;
      default:
        setDraft(null);
    }
    setModal(id);
  }

  function applyModal() {
    if (!draft || !modal) return;
    setErr('');
    switch (modal) {
      case 'pageHero':
        setField('pageHeroImage', draft.pageHeroImage);
        break;
      case 'homeCopy':
        setForm((f) => ({
          ...f,
          label: draft.label,
          headingLine1: draft.headingLine1,
          headingLine2: draft.headingLine2,
          intro: draft.intro,
        }));
        break;
      case 'homeVideo':
        setForm((f) => ({
          ...f,
          videoUrl: draft.videoUrl,
          videoAriaLabel: draft.videoAriaLabel,
          videoPosterUrl: draft.videoPosterUrl,
        }));
        break;
      case 'homeYoutube':
        setForm((f) => ({
          ...f,
          youtubeVideoId: draft.youtubeVideoId,
          youtubeIframeTitle: draft.youtubeIframeTitle,
          sidebarText: draft.sidebarText,
          sidebarCtaLabel: draft.sidebarCtaLabel,
          sidebarCtaHref: draft.sidebarCtaHref,
        }));
        break;
      case 'journey': {
        const years = parseTimelineYears(draft.timelineText);
        setForm((f) => ({
          ...f,
          journey: {
            ...f.journey,
            sectionLabel: draft.sectionLabel,
            title: draft.title,
            body: draft.body,
            visualUrl: draft.visualUrl,
            accentImageUrl: draft.accentImageUrl,
            timelineYears: years,
          },
        }));
        setTimelineText(draft.timelineText);
        break;
      }
      case 'missionVision':
        setForm((f) => ({
          ...f,
          missionVision: {
            ...f.missionVision,
            sectionEyebrow: draft.sectionEyebrow,
            sectionTitle: draft.sectionTitle,
            missionTitle: draft.missionTitle,
            missionBody: draft.missionBody,
            visionTitle: draft.visionTitle,
            visionBody: draft.visionBody,
          },
        }));
        break;
      case 'managementIntro':
        setForm((f) => ({
          ...f,
          managementPanel: {
            ...f.managementPanel,
            sectionEyebrow: draft.sectionEyebrow,
            sectionTitle: draft.sectionTitle,
            intro: draft.intro,
          },
        }));
        break;
      default:
        break;
    }
    closeEditorModal();
  }

  function openTeamCreate() {
    setErr('');
    setTeamDraft(normalizeTeamMemberRow(emptyTeamMember()));
    setTeamModal({ mode: 'add' });
  }

  function normalizeTeamMemberRow(m) {
    if (!m || typeof m !== 'object') return emptyTeamMember();
    return {
      id: typeof m.id === 'string' && m.id.trim() ? m.id.trim() : emptyTeamMember().id,
      name: typeof m.name === 'string' ? m.name : '',
      role: typeof m.role === 'string' ? m.role : '',
      bio: typeof m.bio === 'string' ? m.bio : '',
      image: typeof m.image === 'string' ? m.image : '',
    };
  }

  function openTeamEdit(i) {
    setErr('');
    const m = members[i];
    if (!m) return;
    setTeamDraft(normalizeTeamMemberRow(m));
    setTeamModal({ mode: 'edit', index: i });
  }

  function applyTeamModal() {
    if (!teamDraft || !teamModal) return;
    if (!teamMemberRequiredValid(teamDraft)) {
      setErr('Name, role, bio, and photo are all required.');
      return;
    }
    setErr('');
    const row = normalizeTeamMemberRow(teamDraft);
    if (teamModal.mode === 'add') {
      appendMember(row);
      closeTeamModal();
      return;
    }
    setForm((f) => {
      const arr = [...(f.managementPanel?.members || [])];
      const idx = teamModal.index;
      const prev = arr[idx] && typeof arr[idx] === 'object' ? normalizeTeamMemberRow(arr[idx]) : {};
      arr[idx] = { ...prev, ...row };
      return { ...f, managementPanel: { ...f.managementPanel, members: arr } };
    });
    closeTeamModal();
  }

  async function runUpload(file, applyUrl) {
    if (!file) return;
    setErr('');
    setUploadKey('busy');
    try {
      const { url } = await uploadFile(file);
      applyUrl(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadKey('');
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const journey = {
        ...form.journey,
        timelineYears: parseTimelineYears(timelineText),
      };
      const payload = {
        pageHeroImage: form.pageHeroImage || '',
        label: form.label,
        headingLine1: form.headingLine1,
        headingLine2: form.headingLine2,
        intro: form.intro,
        videoUrl: form.videoUrl,
        videoPosterUrl: form.videoPosterUrl,
        videoAriaLabel: form.videoAriaLabel,
        youtubeVideoId: form.youtubeVideoId,
        youtubeIframeTitle: form.youtubeIframeTitle,
        sidebarText: form.sidebarText,
        sidebarCtaLabel: form.sidebarCtaLabel,
        sidebarCtaHref: form.sidebarCtaHref,
        journey,
        missionVision: form.missionVision,
        managementPanel: form.managementPanel,
      };
      const { about } = await patchAbout(payload);
      const next = resolveAbout({ about: about || payload });
      setForm(next);
      setTimelineText(timelineYearsToText(next.journey?.timelineYears));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const members = form.managementPanel?.members || [];
  const uploading = uploadKey === 'busy';

  const setDraftField = (key, value) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">About Us</h1>
          <p className="text-sm text-neutral-600">
            Edit sections in modals, then save to publish to the live site.
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-lg bg-brand-olive px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save to site'}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err && !modal && !teamModal ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="grid w-full max-w-none gap-4 md:grid-cols-2">
          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">About page hero image</h2>
              <p className="mt-1 text-sm text-neutral-600">{clip(form.pageHeroImage, 72)}</p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('pageHero')}>
              Edit
            </button>
          </div>

          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">Home — about copy</h2>
              <p className="mt-1 text-sm text-neutral-600">
                {clip([form.label, form.headingLine1, form.headingLine2].filter(Boolean).join(' · '), 100)}
              </p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('homeCopy')}>
              Edit
            </button>
          </div>

          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">Home — looping video & poster</h2>
              <p className="mt-1 text-sm text-neutral-600">{clip(form.videoUrl, 80)}</p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('homeVideo')}>
              Edit
            </button>
          </div>

          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">Home — YouTube & sidebar</h2>
              <p className="mt-1 text-sm text-neutral-600">
                {form.youtubeVideoId ? `Video: ${clip(String(form.youtubeVideoId), 40)}` : 'YouTube hidden (no ID)'}
              </p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('homeYoutube')}>
              Edit
            </button>
          </div>

          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">About page — Our story / journey</h2>
              <p className="mt-1 text-sm text-neutral-600">{clip(form.journey?.title, 90)}</p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('journey')}>
              Edit
            </button>
          </div>

          <div className={cardClass}>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900">About page — Mission & vision</h2>
              <p className="mt-1 text-sm text-neutral-600">{clip(form.missionVision?.sectionTitle, 90)}</p>
            </div>
            <button type="button" className={editBtnClass} onClick={() => openModal('missionVision')}>
              Edit
            </button>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm md:col-span-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-neutral-900">About page — Management panel</h2>
                <p className="mt-1 text-sm text-neutral-600">{clip(form.managementPanel?.intro, 120)}</p>
                <p className="mt-2 text-xs text-neutral-500">
                  {members.length} {members.length === 1 ? 'person' : 'people'}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button type="button" className={secondaryBtnClass} onClick={() => openModal('managementIntro')}>
                  Section header
                </button>
                <button type="button" className={editBtnClass} onClick={openTeamCreate}>
                  Add person
                </button>
              </div>
            </div>
            {members.length ? (
              <ul className="m-0 mt-4 list-none divide-y divide-neutral-100 border-t border-neutral-100 p-0">
                {members.map((m, i) => (
                  <li key={m.id || i} className="flex flex-wrap items-center justify-between gap-2 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-900">{m.name || '(unnamed)'}</p>
                      <p className="text-xs text-neutral-600">{m.role || '—'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className={secondaryBtnClass} onClick={() => openTeamEdit(i)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-50"
                        onClick={() => removeMember(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">No people yet. Use “Add person” to create one.</p>
            )}
          </div>
        </div>
      </div>

      {/* —— Modals —— */}
      <AdminModal
        open={modal === 'pageHero'}
        title="About page hero image"
        description="Shown behind the top banner on /about. Falls back to the video poster if empty."
        error={err}
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'pageHero' ? (
          <div className="space-y-4">
            <div className="aspect-[21/9] max-h-44 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
              {draft.pageHeroImage ? (
                <img src={draft.pageHeroImage} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full min-h-[4rem] items-center justify-center text-xs text-neutral-400">
                  No image
                </div>
              )}
            </div>
            <label className={label}>
              Upload
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className="mt-1 text-sm file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-sm"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setDraftField('pageHeroImage', url));
                }}
              />
            </label>
            
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'homeCopy'}
        title="Home — about section copy"
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'homeCopy' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Eyebrow label
              <input className={input} value={draft.label} onChange={(e) => setDraftField('label', e.target.value)} />
            </label>
            <label className={label}>
              Heading line 1
              <input
                className={input}
                value={draft.headingLine1}
                onChange={(e) => setDraftField('headingLine1', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Heading line 2
              <input
                className={input}
                value={draft.headingLine2}
                onChange={(e) => setDraftField('headingLine2', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Intro paragraph
              <textarea
                className={`${input} min-h-[6rem]`}
                value={draft.intro}
                onChange={(e) => setDraftField('intro', e.target.value)}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'homeVideo'}
        title="Home — looping video & poster"
        error={err}
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'homeVideo' ? (
          <div className="space-y-4">
            <label className={label}>
              MP4 URL
              <input
                className={input}
                value={draft.videoUrl}
                onChange={(e) => setDraftField('videoUrl', e.target.value)}
              />
            </label>
            <label className={label}>
              Video short description (accessibility)
              <input
                className={input}
                value={draft.videoAriaLabel}
                onChange={(e) => setDraftField('videoAriaLabel', e.target.value)}
              />
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="aspect-video overflow-hidden rounded-lg border border-neutral-200 bg-white">
                {draft.videoPosterUrl ? (
                  <img src={draft.videoPosterUrl} alt="" className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full min-h-[5rem] items-center justify-center text-xs text-neutral-400">
                    No poster
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <label className={`${label} mb-0`}>
                  Upload poster
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    className="mt-1 text-sm file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-sm"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      runUpload(f, (url) => setDraftField('videoPosterUrl', url));
                    }}
                  />
                </label>
                <label className={label}>
                  Poster URL (read-only after upload)
                  <input
                    className={`${input} bg-neutral-50`}
                    value={draft.videoPosterUrl}
                    readOnly
                  />
                </label>
                {draft.videoPosterUrl ? (
                  <button
                    type="button"
                    onClick={() => setDraftField('videoPosterUrl', '')}
                    className="text-xs font-medium text-neutral-600 underline"
                  >
                    Clear poster
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'homeYoutube'}
        title="Home — YouTube embed & sidebar"
        description="Leave the YouTube field empty to hide the embed column on the home page."
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'homeYoutube' ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              YouTube video ID or URL
              <input
                className={input}
                value={draft.youtubeVideoId}
                onChange={(e) => setDraftField('youtubeVideoId', e.target.value)}
                placeholder="e.g. LSUjqOKnXUE"
              />
            </label>
            <label className={label}>
              Iframe title (accessibility)
              <input
                className={input}
                value={draft.youtubeIframeTitle}
                onChange={(e) => setDraftField('youtubeIframeTitle', e.target.value)}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Sidebar text
              <textarea
                className={`${input} min-h-[4rem]`}
                value={draft.sidebarText}
                onChange={(e) => setDraftField('sidebarText', e.target.value)}
              />
            </label>
            <label className={label}>
              Sidebar button label
              <input
                className={input}
                value={draft.sidebarCtaLabel}
                onChange={(e) => setDraftField('sidebarCtaLabel', e.target.value)}
              />
            </label>
            <label className={label}>
              Sidebar button href
              <input
                className={input}
                value={draft.sidebarCtaHref}
                onChange={(e) => setDraftField('sidebarCtaHref', e.target.value)}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'journey'}
        title="About page — Our story / journey"
        error={err}
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'journey' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section eyebrow
              <input
                className={input}
                value={draft.sectionLabel}
                onChange={(e) => setDraftField('sectionLabel', e.target.value)}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={draft.title}
                onChange={(e) => setDraftField('title', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Body
              <textarea
                className={`${input} min-h-[6rem]`}
                value={draft.body}
                onChange={(e) => setDraftField('body', e.target.value)}
              />
            </label>
        
            <label className={`${label} sm:col-span-2`}>
              Timeline years (one per line or comma-separated)
              <textarea
                className={`${input} min-h-[5rem] font-mono text-sm`}
                value={draft.timelineText}
                onChange={(e) => setDraftField('timelineText', e.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-4 sm:col-span-2">
              <label className={`${label} mb-0`}>
                Upload main visual
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  className="mt-1 text-sm file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-sm"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    runUpload(f, (url) => setDraftField('visualUrl', url));
                  }}
                />
              </label>
              <label className={`${label} mb-0`}>
                Upload accent image
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  className="mt-1 text-sm file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-sm"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    runUpload(f, (url) => setDraftField('accentImageUrl', url));
                  }}
                />
              </label>
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'missionVision'}
        title="About page — Mission & vision"
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'missionVision' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section eyebrow
              <input
                className={input}
                value={draft.sectionEyebrow}
                onChange={(e) => setDraftField('sectionEyebrow', e.target.value)}
              />
            </label>
            <label className={label}>
              Section title
              <input
                className={input}
                value={draft.sectionTitle}
                onChange={(e) => setDraftField('sectionTitle', e.target.value)}
              />
            </label>
            <label className={label}>
              Mission card title
              <input
                className={input}
                value={draft.missionTitle}
                onChange={(e) => setDraftField('missionTitle', e.target.value)}
              />
            </label>
            <label className={label}>
              Vision card title
              <input
                className={input}
                value={draft.visionTitle}
                onChange={(e) => setDraftField('visionTitle', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Mission body
              <textarea
                className={`${input} min-h-[5rem]`}
                value={draft.missionBody}
                onChange={(e) => setDraftField('missionBody', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Vision body
              <textarea
                className={`${input} min-h-[5rem]`}
                value={draft.visionBody}
                onChange={(e) => setDraftField('visionBody', e.target.value)}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={modal === 'managementIntro'}
        title="Management panel — section header"
        description="Eyebrow, title, and intro above the people grid on /about."
        onClose={closeEditorModal}
        footer={<ModalFooter onCancel={closeEditorModal} onApply={applyModal} />}
      >
        {draft && modal === 'managementIntro' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section eyebrow
              <input
                className={input}
                value={draft.sectionEyebrow}
                onChange={(e) => setDraftField('sectionEyebrow', e.target.value)}
              />
            </label>
            <label className={label}>
              Section title
              <input
                className={input}
                value={draft.sectionTitle}
                onChange={(e) => setDraftField('sectionTitle', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Intro paragraph
              <textarea
                className={`${input} min-h-[4rem]`}
                value={draft.intro}
                onChange={(e) => setDraftField('intro', e.target.value)}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={Boolean(teamModal)}
        title={teamModal?.mode === 'edit' ? 'Edit team member' : 'Add team member'}
        description="Name, role, bio, and photo are required for each person on the public About page."
        error={err}
        onClose={closeTeamModal}
        panelRef={teamModalPanelRef}
        footer={
          <TeamMemberModalFooter
            onCancel={closeTeamModal}
            onApply={applyTeamModal}
            applyLabel={teamModal?.mode === 'edit' ? 'Save person' : 'Add person'}
            fieldsValid={teamMemberRequiredValid(teamDraft)}
            dodgeEnabled={teamModal?.mode === 'add'}
            draftSnapshot={{
              name: teamDraft?.name,
              role: teamDraft?.role,
              bio: teamDraft?.bio,
              image: teamDraft?.image,
            }}
            nameFieldRef={teamNameInputRef}
            roleFieldRef={teamRoleInputRef}
            bioFieldRef={teamBioInputRef}
            imageFieldRef={teamImageInputRef}
            reducedMotion={prefersReducedMotion}
            fleeBoundsRef={teamModalPanelRef}
          />
        }
      >
        {teamDraft ? (
          <div
            className="space-y-4"
            key={teamModal?.mode === 'edit' ? `member-${teamModal.index}-${teamDraft.id}` : 'member-new'}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
              <div className={`${label} w-full shrink-0 md:w-[min(100%,13.75rem)]`}>
                <span className="block">Photo (required)</span>
                <input
                  ref={teamImageInputRef}
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  className="mt-1 w-full text-sm file:mr-2 file:rounded file:border-0 file:bg-neutral-200 file:px-2 file:py-1 file:text-sm"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    runUpload(f, (url) =>
                      setTeamDraft((d) => {
                        if (!d) return d;
                        return { ...d, image: url };
                      })
                    );
                  }}
                />
                {teamDraft.image ? (
                  <img
                    src={teamDraft.image}
                    alt=""
                    className="mt-2 aspect-[4/5] w-full rounded-md border border-neutral-200 object-cover"
                    key={teamDraft.image}
                  />
                ) : (
                  <div
                    className="mt-2 flex aspect-[4/5] w-full items-center justify-center rounded-md border border-dashed border-neutral-300 bg-neutral-50 text-center text-xs text-neutral-500"
                    aria-hidden
                  >
                    Preview
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1 grid gap-3 sm:grid-cols-2">
                <label className={label}>
                  Name
                  <input
                    ref={teamNameInputRef}
                    className={input}
                    value={teamDraft.name ?? ''}
                    onChange={(e) => setTeamDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                    autoComplete="name"
                  />
                </label>
                <label className={label}>
                  Role
                  <input
                    ref={teamRoleInputRef}
                    className={input}
                    value={teamDraft.role ?? ''}
                    onChange={(e) => setTeamDraft((d) => (d ? { ...d, role: e.target.value } : d))}
                    autoComplete="organization-title"
                  />
                </label>
                <label className={`${label} sm:col-span-2`}>
                  Bio
                  <textarea
                    ref={teamBioInputRef}
                    className={`${input} min-h-[6rem] sm:min-h-[7.5rem]`}
                    value={teamDraft.bio ?? ''}
                    onChange={(e) => setTeamDraft((d) => (d ? { ...d, bio: e.target.value } : d))}
                  />
                </label>
              </div>
            </div>
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
